/* useSupportEmail — fetches the SUPPORT_EMAIL once from `/api/config/public`
   and caches it in module scope.

   Why module-level cache (vs. context provider):
   - The address never changes during a session and is safe to leak (it's
     literally meant to be displayed everywhere).
   - Avoids re-render storms when many footers / pages mount at once.
   - One in-flight request is shared across all callers via a promise.

   The fallback is the same value we hard-code in the backend, so a missing
   env var, a 404 from a stale deploy, or even a hostile network all still
   yield a clickable mailto link.
*/
import { useEffect, useState } from "react";
import { API } from "@/lib/api";

const FALLBACK = "supporto@aibridalmanager.com";

let _cached = "";
let _inflight = null;

async function fetchSupportEmail() {
  if (_cached) return _cached;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      // NOTE: bare fetch (not the `api` axios instance) so this works
      // identically on the landing page where the user is logged out
      // and on the in-app footer once authenticated. No cookies needed.
      const r = await fetch(`${API}/config/public`, { credentials: "omit" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      _cached = (j && typeof j.support_email === "string" && j.support_email.trim()) || FALLBACK;
    } catch {
      _cached = FALLBACK;
    } finally {
      _inflight = null;
    }
    return _cached;
  })();
  return _inflight;
}

export default function useSupportEmail() {
  const [email, setEmail] = useState(_cached || FALLBACK);
  useEffect(() => {
    let alive = true;
    fetchSupportEmail().then((v) => { if (alive) setEmail(v); });
    return () => { alive = false; };
  }, []);
  return email;
}
